
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Input, Select, TextArea } from '../components/Input';
import { PlantClassification, SunExposure, PlantStatus, Reminders, PlantKingdom } from '../types';
import { 
  Camera, MapPin, Save, X, Bell, AlertCircle, Loader2, Sparkles, Activity, 
  Map as MapIcon, AlarmClock, Clock, Droplets, FlaskConical, Scissors, Sprout,
  Image as ImageIcon, Wand2, Plus
} from 'lucide-react';
import { MapPicker } from '../components/MapPicker';
import { storage } from '../services/storage';
import { t } from '../services/i18n';
import { fileToBase64Compressed } from '../services/imageUtils';

export const PlantForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'info' | 'care' | 'photos' | 'health' | 'loc'>('info');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);

  const handleGeocode = async () => {
    if (!formData.localizacao.descricao) return;
    setIsGeocoding(true);
    try {
      const loc = await storage.geocodeLocation(formData.localizacao.descricao);
      if (loc) {
        setFormData(p => ({
          ...p,
          localizacao: {
            ...p.localizacao,
            latitude: loc.latitude,
            longitude: loc.longitude,
            descricao: loc.descricao || p.localizacao.descricao
          }
        }));
      } else {
        alert("Não foi possível encontrar as coordenadas.");
      }
    } catch (e) {
      console.error(e);
      alert("Erro ao buscar coordenadas.");
    } finally {
      setIsGeocoding(false);
    }
  };
  const [fetching, setFetching] = useState(!!id);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [isBotanyLoading, setIsBotanyLoading] = useState(false);
  const [showManualKingdom, setShowManualKingdom] = useState(false);
  const [showManualCategory, setShowManualCategory] = useState(false);
  
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    nomeComum: '',
    nomeCientifico: '',
    genero: '',
    familia: '',
    categoria: '' as any,
    reinoPlantae: '' as any,
    dataAquisicao: new Date().toISOString().split('T')[0],
    origem: 'comprada' as any,
    observacoes: '',
    status: PlantStatus.HEALTHY,
    planoManejo: {
      rega: '',
      adubacao: '',
      tipoAdubo: '',
      poda: '',
      exposicaoSolar: '' as any,
    },
    tratamento: {
      medicacao: '',
      controlePragas: '',
      instrucoesEspeciais: '',
    },
    localizacao: {
      latitude: 0,
      longitude: 0,
      descricao: '',
    },
    imagens: [] as string[],
    ultimaRega: '',
    reminders: {
      watering: false,
      fertilizing: false,
      pruning: false,
      repotting: false
    } as Reminders,
  });

  useEffect(() => {
    const scanData = location.state as { scanResult?: string; scanImage?: string } | null;
    if (scanData?.scanResult && !id) {
      setFormData(prev => ({
        ...prev,
        observacoes: `[Diagnóstico IA]: ${scanData.scanResult}\n\n${prev.observacoes}`,
        imagens: scanData.scanImage ? [scanData.scanImage] : []
      }));
    }

    if (id) {
        const loadPlant = async () => {
          try {
            const plant = await storage.getPlantById(id);
            if (plant) {
                setFormData({
                    ...plant,
                    nomeCientifico: plant.nomeCientifico || '',
                    genero: plant.genero || '',
                    familia: plant.familia || '',
                    reinoPlantae: plant.reinoPlantae || '' as any,
                    observacoes: plant.observacoes || '',
                    planoManejo: {
                      rega: plant.planoManejo?.rega || '',
                      adubacao: plant.planoManejo?.adubacao || '',
                      tipoAdubo: plant.planoManejo?.tipoAdubo || '',
                      poda: plant.planoManejo?.poda || '',
                      exposicaoSolar: plant.planoManejo?.exposicaoSolar || '',
                    },
                    tratamento: {
                      medicacao: plant.tratamento?.medicacao || '',
                      controlePragas: plant.tratamento?.controlePragas || '',
                      instrucoesEspeciais: plant.tratamento?.instrucoesEspeciais || '',
                    },
                    localizacao: {
                      latitude: plant.localizacao?.latitude || 0,
                      longitude: plant.localizacao?.longitude || 0,
                      descricao: plant.localizacao?.descricao || '',
                    },
                    imagens: plant.imagens || [],
                    ultimaRega: plant.ultimaRega || '',
                    reminders: plant.reminders || { watering: false, fertilizing: false, pruning: false, repotting: false },
                });
            }
          } catch (err) {
            setError(t('form_error_botanical_db'));
          } finally {
            setFetching(false);
          }
        };
        loadPlant();
    }
  }, [id, location.state]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...(prev as any)[parent], [child]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (error) setError(null);
  };

  const handleBotanicalLookup = async () => {
    if (!formData.nomeCientifico.trim()) {
      setError(t('form_error_scientific_name'));
      return;
    }
    setIsBotanyLoading(true);
    setError(null);
    try {
      const info = await storage.getBotanicalInfo(formData.nomeCientifico);
      if (info.family || info.genus || info.scientificName || info.reinoPlantae || info.categoria || info.commonName) {
        setFormData(prev => ({
          ...prev,
          nomeComum: info.commonName || prev.nomeComum,
          nomeCientifico: info.scientificName || prev.nomeCientifico,
          genero: info.genus || prev.genero,
          familia: info.family || prev.familia,
          reinoPlantae: info.reinoPlantae || prev.reinoPlantae,
          categoria: info.categoria || prev.categoria
        }));
      } else {
        setError(t('form_error_botanical_lookup'));
      }
    } catch (err) {
      setError(t('form_error_botanical_db'));
    } finally {
      setIsBotanyLoading(false);
    }
  };

  const handleReminderChange = (key: keyof Reminders) => {
    setFormData(prev => ({
      ...prev,
      reminders: { ...prev.reminders, [key]: !prev.reminders[key] }
    }));
  };

  const handleScheduleCalendar = (key: keyof Reminders) => {
    if (!formData.nomeComum) {
      setError(t('form_error_scientific_name'));
      return;
    }

    const titles: Record<keyof Reminders, string> = {
      watering: `🌿 ALARME: ${t('form_reminder_watering')} ${formData.nomeComum}`,
      fertilizing: `🧪 ALARME: ${t('form_reminder_fertilizing')} ${formData.nomeComum}`,
      pruning: `✂️ ALARME: ${t('form_reminder_pruning')} ${formData.nomeComum}`,
      repotting: `🪴 ALARME: ${t('form_reminder_repotting')} ${formData.nomeComum}`
    };

    const title = titles[key];
    const desc = t('form_calendar_desc');
    const now = new Date();
    const startDate = new Date(now.getTime() + 60 * 60 * 1000);
    const endDate = new Date(startDate.getTime() + 15 * 60 * 1000);

    const formatICSDate = (date: Date) => date.toISOString().replace(/-|:|\.\d\d\d/g, "");

    const icsContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "PRODID:-//Raizama Garden//CARE//PT",
      "BEGIN:VEVENT",
      `UID:${id || 'new'}-${key}-${now.getTime()}@raizamagarden.care`,
      `DTSTAMP:${formatICSDate(now)}`,
      `DTSTART:${formatICSDate(startDate)}`,
      `DTEND:${formatICSDate(endDate)}`,
      `SUMMARY:${title}`,
      `DESCRIPTION:${desc}`,
      "BEGIN:VALARM",
      "ACTION:DISPLAY",
      "DESCRIPTION:" + t('form_calendar_alarm_desc'),
      "TRIGGER:-PT0M",
      "END:VALARM",
      "END:VEVENT",
      "END:VCALENDAR"
    ].join("\r\n");

    const calendarUrl = `data:text/calendar;charset=utf8,${encodeURIComponent(icsContent)}`;
    window.location.assign(calendarUrl);
  };

  const handleAiSuggestFertilizer = async () => {
    if (!formData.categoria) {
      setError(t('form_error_category_first'));
      return;
    }
    setIsSuggesting(true);
    try {
      const suggestion = await storage.suggestFertilizer(formData.categoria, formData.observacoes);
      setFormData(prev => ({
        ...prev,
        planoManejo: { ...prev.planoManejo, tipoAdubo: suggestion }
      }));
    } finally {
      setIsSuggesting(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setLoading(true); // Mostra loading durante a compressão
    try {
      const compressedBase64 = await fileToBase64Compressed(file);
      setFormData(prev => ({ ...prev, imagens: [...prev.imagens, compressedBase64] }));
      setError(null);
    } catch (err) {
      console.error("Erro ao comprimir imagem:", err);
      setError("Erro ao processar imagem. Tente uma foto menor.");
    } finally {
      setLoading(false);
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Apenas Nome Científico é obrigatório
    if (!formData.nomeCientifico.trim()) {
        setError(t('form_error_scientific_name'));
        return;
    }
    
    setLoading(true);
    try {
      if (id) {
        await storage.updatePlant(id, formData as any);
        navigate(`/plant/${id}`);
      } else {
        const newPlantId = await storage.addPlant(formData as any);
        navigate('/collection', { state: { newPlantId } });
      }
    } catch (err: any) {
      setError(err.message || t('form_error_save'));
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <div className="flex justify-center p-20"><Loader2 className="animate-spin text-raizama-action" /></div>;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <input type="file" accept="image/*" capture="environment" className="hidden" ref={cameraInputRef} onChange={handleImageUpload} />
      <input type="file" accept="image/*" className="hidden" ref={galleryInputRef} onChange={handleImageUpload} />

      {error && (
        <div className="bg-red-500/20 border border-red-500/50 p-4 rounded-xl flex items-center gap-3 text-red-400 text-sm animate-fade-in sticky top-20 z-30 backdrop-blur-md">
          <AlertCircle size={20} className="shrink-0" />
          <span className="font-medium">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto p-1"><X size={16} /></button>
        </div>
      )}

      <div className="flex bg-raizama-surface/80 rounded-2xl p-1.5 border border-raizama-border/20 shadow-lg overflow-x-auto no-scrollbar">
        {(['info', 'care', 'photos', 'health', 'loc'] as const).map(tab => (
           <button 
            key={tab} 
            type="button" 
            onClick={() => setActiveTab(tab)} 
            className={`flex-1 min-w-[70px] py-3 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all duration-300 ${activeTab === tab ? 'bg-raizama-action text-white shadow-lg' : 'text-raizama-textSec hover:text-white'}`}
          >
             {tab === 'info' ? t('form_tab_info') : tab === 'care' ? t('form_tab_care') : tab === 'photos' ? t('form_tab_photos') : tab === 'health' ? t('form_tab_health') : t('form_tab_loc')}
           </button>
        ))}
      </div>

      <div className="flex-1">
        {activeTab === 'info' && (
            <div className="animate-fade-in space-y-4">
                <Input label={t('form_common_name')} name="nomeComum" placeholder={t('form_placeholder_common')} value={formData.nomeComum} onChange={handleInputChange} />
                
                <Input 
                  label={t('form_scientific_name')} 
                  name="nomeCientifico" 
                  placeholder={t('form_placeholder_scientific')} 
                  value={formData.nomeCientifico} 
                  onChange={handleInputChange} 
                  required
                  rightElement={
                    <button 
                      type="button" 
                      onClick={handleBotanicalLookup} 
                      disabled={isBotanyLoading}
                      title={t('form_ai_fill')}
                      className={`p-2 rounded-lg transition-all ${isBotanyLoading ? 'text-raizama-action bg-raizama-action/10' : 'text-raizama-textSec hover:text-raizama-action hover:bg-raizama-action/5'}`}
                    >
                      {isBotanyLoading ? <Loader2 size={18} className="animate-spin" /> : <Wand2 size={18} />}
                    </button>
                  }
                />
                
                <div className="grid grid-cols-2 gap-4">
                    <Input label={t('form_genus')} name="genero" placeholder={t('form_placeholder_genus')} value={formData.genero} onChange={handleInputChange} />
                    <Input label={t('form_family')} name="familia" placeholder={t('form_placeholder_family')} value={formData.familia} onChange={handleInputChange} />
                </div>

                {(!formData.reinoPlantae && !showManualKingdom && !id) ? (
                  <div className="flex flex-col gap-1 mb-4">
                    <label className="text-[10px] font-bold text-raizama-textMain uppercase tracking-widest ml-1">{t('form_kingdom')}</label>
                    <button 
                      type="button"
                      onClick={() => setShowManualKingdom(true)}
                      className="w-full bg-raizama-surface/40 border border-dashed border-raizama-border/30 rounded-lg p-3 text-raizama-textSec text-xs flex items-center justify-center gap-2 hover:border-raizama-action hover:text-white transition-all"
                    >
                      <Plus size={14} /> {t('form_select_manual_kingdom') || 'Selecionar Reino Manualmente'}
                    </button>
                  </div>
                ) : (
                  <Select 
                    label={t('form_kingdom')} 
                    name="reinoPlantae" 
                    value={formData.reinoPlantae} 
                    onChange={handleInputChange} 
                    options={[
                      { label: t('enum_briofitas'), value: PlantKingdom.BRIOFITAS },
                      { label: t('enum_pteridofitas'), value: PlantKingdom.PTERIDOFITAS },
                      { label: t('enum_gimnospermas'), value: PlantKingdom.GIMNOSPERMAS },
                      { label: t('enum_angiospermas'), value: PlantKingdom.ANGIOSPERMAS },
                    ]} 
                  />
                )}

                <div className="grid grid-cols-2 gap-4">
                  {(!formData.categoria && !showManualCategory && !id) ? (
                    <div className="flex flex-col gap-1 mb-4">
                      <label className="text-[10px] font-bold text-raizama-textMain uppercase tracking-widest ml-1">{t('form_category')}</label>
                      <button 
                        type="button"
                        onClick={() => setShowManualCategory(true)}
                        className="w-full h-[46px] bg-raizama-surface/40 border border-dashed border-raizama-border/30 rounded-lg p-3 text-raizama-textSec text-[10px] flex items-center justify-center gap-2 hover:border-raizama-action hover:text-white transition-all uppercase font-bold"
                      >
                        <Plus size={14} /> {t('form_select_manual_category') || 'Categoria'}
                      </button>
                    </div>
                  ) : (
                    <Select 
                      label={t('form_category')} 
                      name="categoria" 
                      value={formData.categoria} 
                      onChange={handleInputChange} 
                      options={[
                        { label: t('enum_succulents_cacti'), value: PlantClassification.SUCCULENTS_CACTI },
                        { label: t('enum_tropical_foliage'), value: PlantClassification.TROPICAL_FOLIAGE },
                        { label: t('enum_orchids'), value: PlantClassification.ORCHIDS },
                        { label: t('enum_trees_shrubs'), value: PlantClassification.TREES_SHRUBS },
                        { label: t('enum_aquatic'), value: PlantClassification.AQUATIC },
                        { label: t('enum_edible_garden'), value: PlantClassification.EDIBLE_GARDEN },
                        { label: t('enum_caudiciform'), value: PlantClassification.CAUDICIFORM },
                        { label: t('enum_medicinal'), value: PlantClassification.MEDICINAL },
                      ]} 
                    />
                  )}
                  <Select 
                    label={t('form_status')} 
                    name="status" 
                    value={formData.status} 
                    onChange={handleInputChange} 
                    options={[
                      { label: t('enum_healthy'), value: PlantStatus.HEALTHY },
                      { label: t('enum_observation'), value: PlantStatus.OBSERVATION },
                      { label: t('enum_sick'), value: PlantStatus.SICK },
                    ]} 
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                    <Input label={t('form_date_acquisition')} type="date" name="dataAquisicao" value={formData.dataAquisicao} onChange={handleInputChange} />
                    <Select 
                      label={t('form_origin')} 
                      name="origem" 
                      value={formData.origem} 
                      onChange={handleInputChange} 
                      options={[
                        {label: t('details_origin_bought'), value: 'comprada'}, 
                        {label: t('details_origin_collected'), value: 'coletada'}, 
                        {label: t('details_origin_cultivated'), value: 'cultivada'}, 
                        {label: t('details_origin_clone'), value: 'clone'}, 
                        {label: t('details_origin_seed'), value: 'semeio'}, 
                        {label: t('details_origin_gift'), value: 'presente'}
                      ]} 
                    />
                </div>
                <TextArea label={t('form_notes')} name="observacoes" placeholder={t('form_placeholder_notes')} value={formData.observacoes} onChange={handleInputChange} />
            </div>
        )}

        {activeTab === 'care' && (
            <div className="animate-fade-in space-y-4">
                <Select 
                  label={t('form_sun_exposure')} 
                  name="planoManejo.exposicaoSolar" 
                  value={formData.planoManejo.exposicaoSolar} 
                  onChange={handleInputChange} 
                  options={[
                    { label: t('enum_full_sun'), value: SunExposure.FULL_SUN },
                    { label: t('enum_partial_shade'), value: SunExposure.PARTIAL_SHADE },
                    { label: t('enum_shade'), value: SunExposure.SHADE },
                  ]} 
                />
                <div className="grid grid-cols-2 gap-4">
                    <Input label={t('form_watering_freq')} name="planoManejo.rega" placeholder={t('form_placeholder_watering')} value={formData.planoManejo.rega} onChange={handleInputChange} />
                    <Input label={t('form_fertilizing_cycle')} name="planoManejo.adubacao" placeholder={t('form_placeholder_fertilizing')} value={formData.planoManejo.adubacao} onChange={handleInputChange} />
                </div>
                <Input label={t('form_last_watering')} type="datetime-local" name="ultimaRega" value={formData.ultimaRega ? formData.ultimaRega.substring(0, 16) : ''} onChange={(e) => setFormData(prev => ({...prev, ultimaRega: e.target.value}))} />
                <Input 
                  label={t('form_fertilizer_type')} 
                  name="planoManejo.tipoAdubo" 
                  value={formData.planoManejo.tipoAdubo} 
                  onChange={handleInputChange} 
                  placeholder={t('form_placeholder_fertilizer_type')} 
                  rightElement={
                    <button type="button" onClick={handleAiSuggestFertilizer} disabled={isSuggesting} className="p-2 text-raizama-action hover:bg-raizama-action/10 rounded-lg">
                      {isSuggesting ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                    </button>
                  }
                />
                
                <div className="bg-raizama-surface/50 p-6 rounded-3xl border border-raizama-border/20 mt-6 shadow-xl">
                    <h3 className="text-[11px] font-black text-raizama-highlight uppercase tracking-[0.2em] flex items-center gap-2 mb-6">
                      <Bell size={16} className="text-raizama-action" /> {t('form_reminders_title')}
                    </h3>
                    <div className="space-y-4">
                        {[
                          { id: 'watering', label: t('form_reminder_watering'), icon: <Droplets size={16} /> },
                          { id: 'fertilizing', label: t('form_reminder_fertilizing'), icon: <FlaskConical size={16} /> },
                          { id: 'pruning', label: t('form_reminder_pruning'), icon: <Scissors size={16} /> },
                          { id: 'repotting', label: t('form_reminder_repotting'), icon: <Sprout size={16} /> }
                        ].map((rem) => {
                            const isRemActive = formData.reminders[rem.id as keyof Reminders];
                            return (
                                <div key={rem.id} className="flex flex-col gap-2">
                                    <div 
                                      onClick={() => handleReminderChange(rem.id as any)}
                                      className={`flex items-center justify-between p-4 rounded-2xl border transition-all cursor-pointer ${isRemActive ? 'bg-raizama-action/5 border-raizama-action shadow-[0_0_15px_rgba(13,149,7,0.1)]' : 'bg-black/20 border-raizama-border/10 hover:border-raizama-border/30'}`}
                                    >
                                        <div className="flex items-center gap-3">
                                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${isRemActive ? 'bg-raizama-action text-white' : 'bg-white/5 text-raizama-textSec'}`}>
                                            {rem.icon}
                                          </div>
                                          <div>
                                            <p className={`text-sm font-bold transition-colors ${isRemActive ? 'text-white' : 'text-raizama-textSec'}`}>{rem.label}</p>
                                            <p className="text-[9px] text-raizama-textSec/60 uppercase font-black tracking-widest">{isRemActive ? t('form_reminder_active') : t('form_reminder_inactive')}</p>
                                          </div>
                                        </div>
                                        <div className={`w-11 h-6 flex items-center rounded-full p-1 transition-colors duration-300 ${isRemActive ? 'bg-raizama-action' : 'bg-gray-700'}`}>
                                          <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${isRemActive ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                        </div>
                                    </div>
                                    {isRemActive && (
                                        <button 
                                            type="button"
                                            onClick={() => handleScheduleCalendar(rem.id as keyof Reminders)}
                                            className="mx-2 mb-2 p-3 bg-raizama-surface border border-raizama-border/30 text-white rounded-xl shadow-lg hover:border-raizama-action transition-all flex items-center justify-center gap-2 animate-fade-in"
                                        >
                                            <AlarmClock size={16} className="text-raizama-action" />
                                            <span className="text-[10px] font-black uppercase tracking-widest">{t('form_schedule_alarm')}</span>
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        )}

        {activeTab === 'photos' && (
          <div className="animate-fade-in space-y-6">
            <div className="bg-raizama-surface rounded-3xl p-6 border border-raizama-border/20 shadow-2xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-raizama-action/10 flex items-center justify-center text-raizama-action">
                  <ImageIcon size={24} />
                </div>
                <div>
                  <h3 className="text-white font-black text-sm uppercase tracking-wider">{t('form_gallery_title')}</h3>
                  <p className="text-[10px] text-raizama-textSec uppercase font-bold">{t('form_gallery_subtitle')}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                  {formData.imagens.map((img, idx) => (
                      <div key={idx} className="aspect-square rounded-2xl overflow-hidden relative group border border-raizama-border/10 shadow-lg">
                          <img src={img} className="w-full h-full object-cover" alt={`Preview ${idx}`} />
                          <button 
                            type="button" 
                            onClick={() => setFormData(p => ({...p, imagens: p.imagens.filter((_, i) => i !== idx)}))} 
                            className="absolute top-1.5 right-1.5 bg-red-600/80 p-1.5 rounded-full text-white shadow-md active:scale-90 transition-transform"
                          >
                            <X size={12} />
                          </button>
                      </div>
                  ))}
                  <div onClick={() => cameraInputRef.current?.click()} className="aspect-square bg-black/30 border-2 border-dashed border-raizama-border/30 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-raizama-action hover:bg-raizama-action/5 transition-all group">
                      <Camera size={24} className="text-raizama-textSec group-hover:text-raizama-action transition-colors mb-1" />
                      <span className="text-[8px] text-raizama-textSec font-black uppercase tracking-widest group-hover:text-white">{t('form_camera')}</span>
                  </div>
                  <div onClick={() => galleryInputRef.current?.click()} className="aspect-square bg-black/30 border-2 border-dashed border-raizama-border/30 rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-raizama-action hover:bg-raizama-action/5 transition-all group">
                      <ImageIcon size={24} className="text-raizama-textSec group-hover:text-raizama-action transition-colors mb-1" />
                      <span className="text-[8px] text-raizama-textSec font-black uppercase tracking-widest group-hover:text-white">{t('form_gallery')}</span>
                  </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'health' && (
            <div className="animate-fade-in space-y-4">
                <div className="bg-raizama-surface rounded-3xl p-6 border border-raizama-border/20 shadow-xl">
                  <h3 className="text-[11px] font-black text-raizama-action mb-6 uppercase tracking-widest flex items-center gap-2">
                    <Activity size={18}/> {t('form_treatments_title')}
                  </h3>
                  <div className="space-y-4">
                    <Input label={t('form_medication')} name="tratamento.medicacao" placeholder={t('form_placeholder_medication')} value={formData.tratamento.medicacao} onChange={handleInputChange} />
                    <Input label={t('form_pest_control')} name="tratamento.controlePragas" placeholder={t('form_placeholder_pest_control')} value={formData.tratamento.controlePragas} onChange={handleInputChange} />
                    <TextArea label={t('form_treatment_instructions')} name="tratamento.instrucoesEspeciais" placeholder={t('form_placeholder_treatment_instructions')} value={formData.tratamento.instrucoesEspeciais} onChange={handleInputChange} />
                  </div>
                </div>
            </div>
        )}

        {activeTab === 'loc' && (
            <div className="animate-fade-in space-y-6 pb-4">
                <div className="bg-raizama-surface rounded-3xl p-5 border border-raizama-border/20 shadow-2xl space-y-4">
                  <div className="flex items-center gap-2">
                    <MapIcon size={18} className="text-raizama-action" />
                    <h3 className="text-[11px] font-black text-white uppercase tracking-widest">{t('form_location_title')}</h3>
                  </div>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Input label={t('form_location_desc') || 'Descrição da Localização'} name="localizacao.descricao" value={formData.localizacao.descricao} onChange={handleInputChange} placeholder={t('form_placeholder_location') || 'Ex: Sítio São José, Quadra 5'} />
                    </div>
                    <button type="button" onClick={handleGeocode} disabled={isGeocoding || !formData.localizacao.descricao} className="mb-4 bg-raizama-action text-white p-3 rounded-xl disabled:opacity-50 hover:brightness-110 active:scale-95 transition-all flex items-center justify-center">
                      {isGeocoding ? <Loader2 size={20} className="animate-spin" /> : <Sparkles size={20} />}
                    </button>
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-inner border border-raizama-border/20 mt-4">
                    <MapPicker 
                      initialLat={formData.localizacao.latitude} 
                      initialLng={formData.localizacao.longitude} 
                      onLocationSelect={(lat, lng) => setFormData(p => ({ ...p, localizacao: { ...p.localizacao, latitude: lat, longitude: lng } }))} 
                    />
                  </div>
                </div>
            </div>
        )}
      </div>

      <div className="sticky bottom-4 z-10 pt-4 px-2">
        <button type="submit" disabled={loading} className="w-full bg-raizama-action text-white font-black py-4 rounded-2xl flex items-center justify-center gap-3 shadow-2xl hover:brightness-110 active:scale-95 transition-all uppercase tracking-widest text-sm disabled:opacity-50">
          {loading ? <Loader2 className="animate-spin" /> : <Save size={20} />}
          {id ? t('form_confirm_edit') : t('form_register_plant')}
        </button>
      </div>
    </form>
  );
};
